import React, { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import useTrans from '@hooks/useTrans'
import { cn } from '@utils/safeTs'
import {
  EditorColorType,
  EditorErrorType,
  EditorImageType,
  EditorStatusType,
  EditorTextType,
  NumeroColoriType,
  PosizioneType,
} from '@utils/types'
import { Alert, Image, Text } from '@components/icons'
import EditorImagesSidebar from '../EditorImagesSidebar/EditorImagesSidebar'
import EditorTextsSidebar from '../EditorTextsSidebar/EditorTextsSidebar'
import EditorRiepilogo from '../EditorRiepilogo/EditorRiepilogo'
import styles from './EditorLeftSidebar.module.sass'
import { useEditorContext } from 'context/EditorContext'

interface Props {
  status: EditorStatusType
  selectedPosizione: PosizioneType & { images?: EditorImageType[]; texts?: EditorTextType[] }
  coloriRilevati: EditorColorType[]
  numeroColoriDisponibili: NumeroColoriType[]
  errors?: EditorErrorType[]
  onAddImage?: (v: any) => void | Promise<void>
  onAddText?: (v: { text: string }) => void
  onChangeNumeroColori?: (v: number) => void
  isNumeroColoriEditable?: boolean
  className?: string
}

const EditorLeftSidebar = (props: Props) => {
  const {
    status,
    selectedPosizione,
    coloriRilevati,
    numeroColoriDisponibili,
    errors,
    onAddImage,
    onAddText,
    onChangeNumeroColori,
    isNumeroColoriEditable = true,
    className = '',
  } = props

  const t = useTrans()
  const { activeCanvas } = useEditorContext()

  const tabs = [
    {
      key: 'images',
      label: t('Immagini'),
      icon: <Image />,
      content: <EditorImagesSidebar status={status} onAdd={onAddImage} errors={errors} />,
      error:
        errors?.some((e) => e.key?.includes('dpi-')) ||
        errors?.some((e) => e.key?.includes('gradienti-')),
    },
    {
      key: 'texts',
      label: t('Testi'),
      icon: <Text />,
      content: <EditorTextsSidebar onAdd={onAddText} />,
      error: false, // TODO
    },
  ]

  const [selectedTab, setSelectedTab] = useState(tabs?.[0])

  useEffect(() => {
    if (activeCanvas?.selectedObject) {
      // apro il tab a seconda del tipo di oggetto selezionato (immagine o testo)
      setSelectedTab(
        activeCanvas?.selectedObject?.etype === 'text'
          ? tabs?.find((t) => t.key === 'texts')
          : tabs?.find((t) => t.key === 'images')
      )
    }
  }, [activeCanvas?.selectedObject])

  return selectedPosizione ? (
    <div className={cn(styles.root, className)}>
      {!selectedPosizione?.configurazione?.numero_colori?.full_color && (
        <EditorRiepilogo
          selectedPosizione={selectedPosizione}
          numeriColori={numeroColoriDisponibili}
          coloriRilevati={coloriRilevati}
          errors={errors}
          onChange={onChangeNumeroColori}
          isNumeroColoriEditable={isNumeroColoriEditable}
        />
      )}
      <div className={styles.tabs}>
        {tabs.map((t) => (
          <button
            key={t.key}
            id={`${t.key}_tab`}
            role="tab"
            type="button"
            aria-selected={t.key === selectedTab?.key}
            aria-controls={`${t.key}_tabpanel`}
            disabled={selectedTab?.key === t.key}
            onClick={() => setSelectedTab(t)}
            className={styles.tabTrigger}
          >
            {!!t.error && (
              <span className={styles.error}>
                <Alert />
              </span>
            )}
            <span className={styles.tabTriggerIcon}>{t.icon}</span>
            <span className={styles.tabTriggerLabel}>{t.label}</span>
          </button>
        ))}
      </div>
      {tabs.map((t) => (
        <div
          key={t.key}
          id={`${t.key}_tabpanel`}
          role="tabpanel"
          aria-labelledby={`${t.key}_tab`}
          className={cn(
            styles.tabContent,
            t.key === selectedTab?.key ? styles['tabContent--selected'] : ''
          )}
        >
          {t.content}
        </div>
      ))}
    </div>
  ) : (
    <></>
  )
}

export default EditorLeftSidebar
